import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import { useServerFieldErrors } from '../../../shared/hooks/useServerFieldErrors'
import type { PlatformAdminItem } from '../adminModel'
import { PLATFORM_ROLES, PLATFORM_ROLE_LABEL, type PlatformRole, type PlatformRoleInfo } from '../platformRoles'

interface FormData {
    nome: string
    email: string
    password: string
    activo: boolean
    /** Não é um input registado — o valor real vive no estado `roles`; aqui só
     *  para o RHF poder guardar o erro do campo (validação cliente + 422). */
    roles: PlatformRole[]
}

interface Props {
    open: boolean
    mode: 'create' | 'edit'
    initial?: PlatformAdminItem | null
    onClose: () => void
    onSaved: () => void
}

export default function AdminFormDialog({ open, mode, initial, onClose, onSaved }: Props) {
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [catalogo, setCatalogo] = useState<PlatformRoleInfo[]>([])
    const [roles, setRoles] = useState<Set<PlatformRole>>(new Set())

    const { register, handleSubmit, reset, watch, setValue, setError, clearErrors, formState: { errors } } =
        useForm<FormData>({ defaultValues: { nome: '', email: '', password: '', activo: true, roles: [] } })
    useServerFieldErrors(setError, clearErrors)

    useEffect(() => {
        if (!open) return
        setApiError('')
        reset({
            nome: initial?.nome ?? '',
            email: initial?.email ?? '',
            password: '',
            activo: initial?.activo ?? true,
            roles: [],
        })
        setRoles(new Set(initial?.roles ?? []))

        api.get<PlatformRoleInfo[]>('/api/admin/platform-admins/roles')
            .then((r) => setCatalogo(r.data ?? []))
            .catch(() => setCatalogo([]))
    }, [open, initial, reset])

    const activo = watch('activo')
    // O próprio admin não pode retirar-se o papel ADMIN (o servidor também bloqueia).
    const adminBloqueado = mode === 'edit' && !!initial?.proprio && roles.has('ADMIN')

    const opcoesPapeis: { role: PlatformRole; rotulo: string; descricao?: string; permissoes?: string[] }[] =
        (catalogo.length > 0 ? catalogo : PLATFORM_ROLES.map((role) => ({ role, rotulo: PLATFORM_ROLE_LABEL[role], descricao: '', permissoes: [] })))
            .map((c) => ({ role: c.role, rotulo: c.rotulo, descricao: c.descricao, permissoes: c.permissoes }))

    const toggleRole = (role: PlatformRole) => {
        if (role === 'ADMIN' && adminBloqueado) return
        clearErrors('roles')
        setRoles((prev) => {
            const next = new Set(prev)
            if (next.has(role)) next.delete(role)
            else next.add(role)
            return next
        })
    }

    const onSubmit = async (data: FormData) => {
        if (roles.size === 0) {
            setError('roles', { type: 'validate', message: 'Indique pelo menos um papel.' })
            return
        }
        setApiError('')
        setSubmitting(true)
        try {
            const listaRoles = [...roles]
            if (mode === 'create') {
                await api.post('/api/admin/platform-admins', {
                    nome: data.nome.trim(),
                    email: data.email.trim(),
                    password: data.password,
                    roles: listaRoles,
                })
            } else if (initial) {
                // PATCH substitui os três campos — enviar sempre os três.
                await api.patch(`/api/admin/platform-admins/${initial.id}`, {
                    nome: data.nome.trim(),
                    activo: data.activo,
                    roles: listaRoles,
                })
            }
            onSaved()
        } catch (err) {
            const be = getBackendError(err)
            // 422 com `fields` → o intercetor global já os aplicou aos campos.
            if (!(be?.fields && Object.keys(be.fields).length > 0)) {
                setApiError(be?.message ?? 'Não foi possível guardar. Tente novamente.')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const rolesError = errors.roles?.message

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {mode === 'create' ? 'Novo administrador' : 'Editar administrador'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                    <TextField
                        label="Nome"
                        autoFocus
                        error={!!errors.nome}
                        helperText={errors.nome?.message}
                        {...register('nome', {
                            required: 'O nome é obrigatório.',
                            maxLength: { value: 150, message: 'Nome demasiado longo.' },
                        })}
                    />

                    <TextField
                        label="Email"
                        type="email"
                        disabled={mode === 'edit'}
                        helperText={mode === 'edit' ? 'O email não pode ser alterado.' : errors.email?.message}
                        error={!!errors.email}
                        {...register('email', {
                            required: mode === 'create' ? 'O email é obrigatório.' : false,
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Insira um email válido.' },
                        })}
                    />

                    {mode === 'create' && (
                        <TextField
                            label="Palavra-passe"
                            type="password"
                            autoComplete="new-password"
                            error={!!errors.password}
                            helperText={errors.password?.message ?? 'Mínimo 8 caracteres.'}
                            {...register('password', {
                                required: 'A palavra-passe é obrigatória.',
                                minLength: { value: 8, message: 'Mínimo 8 caracteres.' },
                            })}
                        />
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Papéis</Typography>
                        <Typography variant="caption" color="text.secondary">
                            As permissões dos papéis somam-se. Pelo menos um.
                        </Typography>
                        <FormGroup sx={{ mt: 1 }}>
                            {opcoesPapeis.map((o) => {
                                const bloqueado = o.role === 'ADMIN' && adminBloqueado
                                const control = (
                                    <FormControlLabel
                                        key={o.role}
                                        sx={{ alignItems: 'flex-start', mb: 0.5 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={roles.has(o.role)}
                                                disabled={bloqueado}
                                                onChange={() => toggleRole(o.role)}
                                                sx={{ pt: 0.5 }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.rotulo}</Typography>
                                                {o.descricao && (
                                                    <Typography variant="caption" color="text.secondary">{o.descricao}</Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                )
                                return o.permissoes && o.permissoes.length > 0 ? (
                                    <Tooltip key={o.role} title={o.permissoes.join(', ')} placement="right">
                                        <Box>{control}</Box>
                                    </Tooltip>
                                ) : (
                                    control
                                )
                            })}
                        </FormGroup>
                        {(rolesError || adminBloqueado) && (
                            <Typography variant="caption" color={rolesError ? 'error' : 'text.secondary'}>
                                {rolesError ?? 'Não pode remover o seu próprio papel de Administrador.'}
                            </Typography>
                        )}
                    </Box>

                    {mode === 'edit' && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={activo}
                                    disabled={initial?.proprio}
                                    onChange={(e) => setValue('activo', e.target.checked, { shouldDirty: true })}
                                />
                            }
                            label={activo ? 'Conta activa' : 'Conta inactiva'}
                        />
                    )}
                    {mode === 'edit' && initial?.proprio && (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            Não pode desactivar a sua própria conta.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'A guardar…' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}
